// --- CSV Export Panel ---
import { useState } from 'react';
import { Download, FileSpreadsheet, Users, Wallet, History, CheckCircle } from 'lucide-react';
import type { Player, Team, Transaction } from '../types';

interface ExportPanelProps {
  players: Player[];
  teams: Team[];
  transactions: Transaction[];
}

const ExportPanel: React.FC<ExportPanelProps> = ({ players, teams, transactions }) => {
  const [exporting, setExporting] = useState<string | null>(null);
  const [lastExported, setLastExported] = useState<Record<string, Date>>({});

  // Helper: Convert array to CSV string
  const arrayToCSV = (data: Record<string, unknown>[], headers: { key: string; label: string }[]): string => {
    const headerRow = headers.map(h => `"${h.label}"`).join(',');
    const rows = data.map(item =>
      headers.map(h => {
        const val = item[h.key];
        if (val === null || val === undefined) return '""';
        if (typeof val === 'string') return `"${val.replace(/"/g, '""')}"`;
        return `"${val}"`;
      }).join(',')
    );
    return [headerRow, ...rows].join('\n');
  };

  // Download CSV file
  const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export Players
  const exportPlayers = () => {
    setExporting('players');
    const headers = [
      { key: 'name', label: 'Player Name' },
      { key: 'mobile_number', label: 'Mobile Number' },
      { key: 'gender', label: 'Gender' },
      { key: 'playing_side', label: 'Playing Side' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'jersey_name', label: 'Jersey Name' },
      { key: 'jersey_number', label: 'Jersey Number' },
      { key: 'tshirt_size', label: 'T-Shirt Size' },
      { key: 'status', label: 'Status' },
      { key: 'team_name', label: 'Team' },
      { key: 'sold_price', label: 'Sold Price' }
    ];

    const data = players.map(p => ({
      name: p.name,
      mobile_number: p.mobile_number,
      gender: p.details?.player_gender || '',
      playing_side: p.details?.playing_side || '',
      date_of_birth: p.details?.date_of_birth || '',
      jersey_name: p.details?.jersey_name || '',
      jersey_number: p.details?.jersey_number || '',
      tshirt_size: p.details?.tshirt_size || '',
      status: p.status,
      team_name: p.teamId ? teams.find(t => t.id === p.teamId)?.name || '' : '',
      sold_price: p.soldPrice || 0
    }));

    const csv = arrayToCSV(data, headers);
    const timestamp = new Date().toISOString().split('T')[0];
    downloadCSV(csv, `mpl-players-${timestamp}.csv`);
    
    setLastExported(prev => ({ ...prev, players: new Date() }));
    setExporting(null);
  };

  // Export Teams
  const exportTeams = () => {
    setExporting('teams');
    const headers = [
      { key: 'name', label: 'Team Name' },
      { key: 'owner_name', label: 'Owner Name' },
      { key: 'total_budget', label: 'Total Budget' },
      { key: 'current_balance', label: 'Current Balance' },
      { key: 'spent', label: 'Amount Spent' },
      { key: 'player_count', label: 'Player Count' },
      { key: 'male_count', label: 'Male Players' },
      { key: 'female_count', label: 'Female Players' },
      { key: 'created_at', label: 'Created At' }
    ];

    const data = teams.map(t => {
      const teamPlayers = players.filter(p => p.teamId === t.id);
      return {
        name: t.name,
        owner_name: t.ownerName,
        total_budget: t.totalBudget,
        current_balance: t.currentBalance,
        spent: t.totalBudget - t.currentBalance,
        player_count: teamPlayers.length,
        male_count: teamPlayers.filter(p => p.details?.player_gender?.toLowerCase() === 'male').length,
        female_count: teamPlayers.filter(p => p.details?.player_gender?.toLowerCase() === 'female').length,
        created_at: t.createdAt
      };
    });

    const csv = arrayToCSV(data, headers);
    const timestamp = new Date().toISOString().split('T')[0];
    downloadCSV(csv, `mpl-teams-${timestamp}.csv`);
    
    setLastExported(prev => ({ ...prev, teams: new Date() }));
    setExporting(null);
  };

  // Export Transactions
  const exportTransactions = () => {
    setExporting('transactions');
    const headers = [
      { key: 'timestamp', label: 'Timestamp' },
      { key: 'type', label: 'Transaction Type' },
      { key: 'performer_name', label: 'Performed By' },
      { key: 'player_name', label: 'Player Name' },
      { key: 'team_name', label: 'Team Name' },
      { key: 'amount', label: 'Amount' },
      { key: 'previous_balance', label: 'Previous Balance' },
      { key: 'new_balance', label: 'New Balance' },
      { key: 'notes', label: 'Notes' }
    ];

    const data = transactions.map(t => ({
      timestamp: t.timestamp,
      type: t.type,
      performer_name: t.performedByName,
      player_name: t.playerName || '',
      team_name: t.teamName || '',
      amount: t.amount || 0,
      previous_balance: t.previousBalance || '',
      new_balance: t.newBalance || '',
      notes: t.notes || ''
    }));

    const csv = arrayToCSV(data, headers);
    const timestamp = new Date().toISOString().split('T')[0];
    downloadCSV(csv, `mpl-transactions-${timestamp}.csv`);
    
    setLastExported(prev => ({ ...prev, transactions: new Date() }));
    setExporting(null);
  };

  // Export card component
  const ExportCard: React.FC<{
    title: string;
    description: string;
    icon: React.ReactNode;
    count: number;
    exportKey: string;
    onExport: () => void;
    color: string;
  }> = ({ title, description, icon, count, exportKey, onExport, color }) => (
    <div className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden`}>
      <div className={`p-4 ${color}`}>
        <div className="flex items-center gap-3 text-white">
          {icon}
          <div>
            <h3 className="font-bold text-lg">{title}</h3>
            <p className="text-white/80 text-sm">{count} records</p>
          </div>
        </div>
      </div>
      <div className="p-4">
        <p className="text-slate-600 text-sm mb-4">{description}</p>
        <div className="flex items-center justify-between">
          <button
            onClick={onExport}
            disabled={exporting === exportKey}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              exporting === exportKey
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            <Download className="w-4 h-4" />
            {exporting === exportKey ? 'Exporting...' : 'Export CSV'}
          </button>
          {lastExported[exportKey] && (
            <span className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="w-3 h-3" />
              Exported
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3">
          <FileSpreadsheet className="w-8 h-8" />
          <div>
            <h2 className="text-2xl font-bold">Export Data</h2>
            <p className="text-white/80">Download your data as CSV files for Excel or other spreadsheet applications</p>
          </div>
        </div>
      </div>

      {/* Export Cards */}
      <div className="grid gap-6 md:grid-cols-3">
        <ExportCard
          title="Players"
          description="Export all player data including personal details, team assignment, and sale information."
          icon={<Users className="w-6 h-6" />}
          count={players.length}
          exportKey="players"
          onExport={exportPlayers}
          color="bg-gradient-to-r from-blue-500 to-indigo-500"
        />
        <ExportCard
          title="Teams"
          description="Export team details with owner info, budget, balance, and player counts."
          icon={<Wallet className="w-6 h-6" />}
          count={teams.length}
          exportKey="teams"
          onExport={exportTeams}
          color="bg-gradient-to-r from-purple-500 to-pink-500"
        />
        <ExportCard
          title="Transactions"
          description="Export complete transaction history with timestamps and balance changes."
          icon={<History className="w-6 h-6" />}
          count={transactions.length}
          exportKey="transactions"
          onExport={exportTransactions}
          color="bg-gradient-to-r from-orange-500 to-red-500"
        />
      </div>
    </div>
  );
};

export default ExportPanel;

